import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { clone, cloneDeep, compact, round, uniqBy } from 'lodash'
import { gqlQuery } from 'app/lib/graphqlClient'
import moment from 'moment'
import { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { BrokenAppointmentsModalContact } from 'app/components/contacts/BrokenAppointmentsModal'
import { ModalType, NewPatientsModal } from 'app/components/contacts/NewPatientsModal'
import { MarketingContactTable } from 'app/components/shared/MarketingContactTable'
import MonthlyLocationBreakdownTable from 'app/components/shared/MonthlyLocationBreakdownTable'
import MetricCell from 'app/components/table/MetricCell'
import { PMSIntegrationCell } from 'app/components/table/PMSIntegrationCell'
import { convertUrlFiltersToES, decodeFilters, FilterOperator, PredefinedFilter } from 'app/filter.utils'
import { closeModal, showModal } from 'app/lib/modal'
import { NewPatientsMetrics, NewPatientsRow, NewPatientsSummary, ScheduledMetric } from 'types/analytics'
import { useHandleSearchParams, useUser } from 'app/utils'
import { bg } from 'lib/background'
import { isSMCOrg } from 'lib/isSMCOrg'
import { debug } from 'lib/logging'
import { formatPercent } from 'app/lib/utils'
import { Jsonify } from 'lib/toJSON'
import { StatsCardType } from 'types/StatsCardType'
import { graphql } from 'app/generated/gql'

const NEW_PATIENTS_METRICS_QUERY = graphql(`
  query NewPatientsMetrics(
    $dateRange: DateRangeInput!
    $compareRange: DateRangeInput
    $channel: String
    $locationIds: [String!]
    $integrationStatus: String
    $filters: String
    $platform: String
    $hideMultiLocationPatients: Boolean
  ) {
    newPatientsMetrics(
      dateRange: $dateRange
      compareRange: $compareRange
      channel: $channel
      locationIds: $locationIds
      integrationStatus: $integrationStatus
      filters: $filters
      platform: $platform
      hideMultiLocationPatients: $hideMultiLocationPatients
    ) {
      summary {
        completed
        scheduled
        reappointments
        reappointmentPercentage
        averageTimeToAppointment
        averageProductionPerNewPatientVisit
        byChannel {
          channel
          count
        }
      }
      comparisonSummary {
        completed
        scheduled
        reappointments
        reappointmentPercentage
        averageTimeToAppointment
        averageProductionPerNewPatientVisit
        byChannel {
          channel
          count
        }
      }
      dailyMetrics {
        bucket
        count
        byChannel {
          channel
          count
        }
      }
      comparisonMetrics {
        bucket
        count
        byChannel {
          channel
          count
        }
      }
      dailyScheduledMetrics {
        bucket
        count
        byChannel {
          channel
          count
        }
      }
      rows {
        bucket
        reappointmentPercentage
        reappointments
        completed
        brokenAppointmentCount
        averageTimeToAppointment
        averageProductionPerNewPatientVisit
        newPatients
        newPatientsFromDigitalMarketing
        newPatientsFromReferralMarketing
        newPatientsFromTraditionalMarketing
        newPatientsFromUnknown
        scheduledNewPatients
        location {
          id
          name
          pmsIntegrated
          lastPmsSyncDate
        }
      }
    }
  }
`)

export const defaultBecameLeadDateFilter: PredefinedFilter = {
  [`becamePatientDate`]: {
    operator: `between`,
    value: [
      moment().subtract(5, `month`).startOf(`month`).format(`YYYY-MM-DD`),
      moment().endOf(`month`).format(`YYYY-MM-DD`),
    ],
    readOnly: true,
  },
}

const locationMonthlyColumns = [
  {
    accessorFn: (row: any) => row.bucket,
    id: `Date`,
    header: `Date`,
    cell: (props: any) => moment.utc(props.getValue()).format(`MMMM YYYY`),
    footer: `Total`,
    meta: { group: `General`, defaultVisible: true, pinned: true },
    enableHiding: false,
  },
  {
    accessorFn: (row: any) => row.reappointmentPercentage,
    cell: (props: any) => <MetricCell value={props.getValue()} type="percentage" />,
    header: `Reappointment Rate`,
    id: `Reappointment Rate`,
    meta: { defaultVisible: true },
  },
  {
    accessorFn: (row: any) => row.brokenAppointmentCount,
    header: `New Patients Broken Appointments`,
    id: `New Patients Broken Appointments`,
    meta: { defaultVisible: true },
  },
  {
    accessorFn: (row: any) => row.averageTimeToAppointment,
    cell: (props: any) => props.getValue() && `${Math.round(props.getValue())} Days`,
    header: `Average Days To Appointment`,
    id: `Average Days To Appointment`,
  },
  {
    accessorFn: (row: any) => row.averageProductionPerNewPatientVisit,
    cell: (props: any) => <MetricCell value={props.getValue()} type="dollars" />,
    header: `Average Production Per New Patient Visit`,
    id: `Average Production Per New Patient Visit`,
  },
  {
    accessorFn: (row: any) => row.newPatients,
    cell: (props: any) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `New Patients`,
    id: `New Patients`,
  },
  {
    accessorFn: (row: any) => row.scheduledNewPatients,
    cell: (props: any) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `Scheduled New Patients`,
    id: `Scheduled New Patients`,
  },
]

const defaultColumns = (fromDate?: string, toDate?: string): ColumnDef<NewPatientsRow, any>[] => [
  {
    enableSorting: true,
    accessorFn: (row) => row.location?.name,
    header: `Location Name`,
    id: `Location Name`,
    meta: {
      onClick: (row: any) =>
        showModal(
          <MonthlyLocationBreakdownTable
            locationIds={[row.bucket!]}
            apiEndpoint="/api/metrics/new-patients"
            columns={locationMonthlyColumns}
          />,
          { title: row.location?.name || ``, isFullScreen: false, is80Percent: true, useRadix: true },
        ),
      defaultVisible: true,
      pinned: true,
    },
    footer: `Total`,
    enableHiding: false,
  },
  {
    accessorFn: (row) => row.location?.pmsIntegrated,
    id: `PMS Integration`,
    header: `PMS Integration`,
    cell: (props) => (
      <PMSIntegrationCell
        isIntegrated={props.getValue()}
        lastPmsSyncDate={props.row.original.location?.lastPmsSyncDate}
      />
    ),
    meta: {
      defaultVisible: true,
      infoTooltip: {
        title: `PMS Integration`,
        message: `Checks to see if there is an active PMS Integration. This can be found on the location settings.`,
      },
    },
    sortUndefined: -1,
  },
  {
    accessorFn: (row) => row.reappointmentPercentage,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    header: `Reappointment Rate`,
    id: `Reappointment Rate`,
    meta: {
      defaultVisible: true,
      infoTooltip: {
        title: `Reappointment Rate`,
        message: `Percentage of contacts that became a patient in the date range that left their appointment with a future appointment scheduled.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={
          (table
            .getFilteredRowModel()
            .rows.reduce((total: number, row: any) => total + (row.original?.reappointments ?? 0), 0) /
            table
              .getFilteredRowModel()
              .rows.reduce((total: number, row: any) => total + (row.original?.completed ?? 0), 0)) *
          100
        }
        type="percentage"
      />
    ),
  },
  {
    accessorFn: (row) => row.brokenAppointmentCount,
    header: `New Patient Broken Appointments`,
    id: `New Patient Broken Appointments`,
    meta: {
      onClick: (row: any) => {
        showModal(
          <BrokenAppointmentsModalContact
            title="New Patient Broken Appointments"
            subtitle={row.location?.name || ``}
            locationId={row.bucket}
            fromDate={fromDate}
            toDate={toDate}
            onClose={closeModal}
          />,
          { hideTitle: true, removePadding: true, isFullScreen: true, useRadix: true },
        )
      },
      defaultVisible: true,
      infoTooltip: {
        title: `New Patient Broken Appointments`,
        message: `An active count of all contacts who have a scheduled first visit in the time frame but have broken the appointment without scheduling a new appointment.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.brokenAppointmentCount, 0)}
        type="unitless"
      />
    ),
  },
  {
    accessorFn: (row) => row.averageTimeToAppointment,
    cell: (props) => props.getValue() && `${Math.round(props.getValue() as number)} Days`,
    header: `Average Days To Appointment`,
    id: `Average Days To Appointment`,
    meta: {
      infoTooltip: {
        title: `Average Days To Appointment`,
        message: `The average length of time in days between the date the appointment record was created and the actual date of the appointment.`,
      },
    },
    footer: ({ table }: any) => {
      const rows = table.getFilteredRowModel().rows
      const totalPatients = rows.reduce((sum: number, row: any) => sum + (row.original.newPatients ?? 0), 0)
      const weightedSum = rows.reduce(
        (sum: number, row: any) => sum + (row.original.averageTimeToAppointment ?? 0) * (row.original.newPatients ?? 0),
        0,
      )
      const avg = totalPatients > 0 ? Math.round(weightedSum / totalPatients) : 0
      return avg ? `${avg} Days` : null
    },
  },
  {
    accessorFn: (row) => row.averageProductionPerNewPatientVisit,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    header: `Average Production Per New Patient Visit`,
    id: `Average Production Per New Patient Visit`,
    meta: {
      infoTooltip: {
        title: `Average Production Per New Patient Visit`,
        message: `The average gross production of the first visit for all contacts who have a became a patient date in the selected date range. The "first visit production" is calculated by adding up the procedure transaction values that are associated with the contacts first visit.`,
      },
    },
    footer: ({ table }: any) => {
      const rows = table.getFilteredRowModel().rows
      const totalPatients = rows.reduce((sum: number, row: any) => sum + (row.original.newPatients ?? 0), 0)
      const weightedSum = rows.reduce(
        (sum: number, row: any) =>
          sum + (row.original.averageProductionPerNewPatientVisit ?? 0) * (row.original.newPatients ?? 0),
        0,
      )
      return totalPatients > 0 ? <MetricCell value={weightedSum / totalPatients} type="dollars" /> : null
    },
  },
  {
    accessorFn: (row) => row.newPatients,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `New Patients`,
    id: `New Patients`,
    meta: {
      onClick: (row: any) => {
        showModal(
          <NewPatientsModal
            locationId={row.bucket}
            fromDate={fromDate}
            toDate={toDate}
            filterBy="New Patients"
            onClose={closeModal}
            title="New Patients"
            subtitle={row.location?.name || ``}
          />,
          { hideTitle: true, removePadding: true, isFullScreen: true, useRadix: true },
        )
      },
      infoTooltip: {
        title: `New Patients`,
        message: `All contacts who have completed a first appointment within the date range.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.newPatients, 0)}
        type="unitless"
      />
    ),
  },
  {
    accessorFn: (row) => row.newPatientsFromDigitalMarketing,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `Digital Marketing NP`,
    id: `Digital Marketing NP`,
    meta: {
      infoTooltip: {
        title: `Digital Marketing New Patients`,
        message: `All contacts with the channel of Digital Marketing who have completed a first appointment within the date range.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.newPatientsFromDigitalMarketing, 0)}
        type="unitless"
      />
    ),
  },
  {
    accessorFn: (row) => row.newPatientsFromReferralMarketing,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `Referral Marketing NP`,
    id: `Referral Marketing NP`,
    meta: {
      infoTooltip: {
        title: `Referral Marketing New Patients`,
        message: `All contacts with the channel of Referral Marketing who have completed a first appointment within the date range.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.newPatientsFromReferralMarketing, 0)}
        type="unitless"
      />
    ),
  },
  {
    accessorFn: (row) => row.newPatientsFromTraditionalMarketing,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `Traditional Marketing NP`,
    id: `Traditional Marketing NP`,
    meta: {
      infoTooltip: {
        title: `Traditional Marketing New Patients`,
        message: `All contacts with the channel of Traditional Marketing who have completed a first appointment within the date range.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.newPatientsFromTraditionalMarketing, 0)}
        type="unitless"
      />
    ),
  },
  {
    accessorFn: (row) => row.newPatientsFromUnknown,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `Unknown Channel NP`,
    id: `Unknown Channel NP`,
    meta: {
      infoTooltip: {
        title: `Unknown Channel New Patients`,
        message: `All contacts with an unknown channel who have completed a first appointment within the date range.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.newPatientsFromUnknown, 0)}
        type="unitless"
      />
    ),
  },
  {
    accessorFn: (row) => row.scheduledNewPatients,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    header: `Scheduled New Patients`,
    id: `Scheduled New Patients`,
    meta: {
      onClick: (row: any) => {
        showModal(
          <NewPatientsModal
            locationId={row.bucket}
            fromDate={fromDate}
            toDate={toDate}
            filterBy="Scheduled New Patients"
            onClose={closeModal}
            title="Scheduled New Patients"
            subtitle={row.location?.name || ``}
          />,
          { hideTitle: true, removePadding: true, isFullScreen: true, useRadix: true },
        )
      },
      infoTooltip: {
        title: `Scheduled New Patients`,
        message: `All contacts who have a first appointment scheduled in the future within the date range.`,
      },
    },
    footer: ({ table }: any) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total: number, row: any) => total + row.original.scheduledNewPatients, 0)}
        type="unitless"
      />
    ),
  },
]

type GroupedData = {
  byChannel: { day: string; tooltipDisplay?: string; values: { [key: string]: number } }[]
  byChannelYoY: { day: string; tooltipDisplay?: string; values: { [key: string]: number } }[]
  byTreatment?: { day: string; values: { [key: string]: number } }[]
}

type ScheduledGroupedItem = {
  day: string
  tooltipDisplay?: string
  value: number
  byChannel: { channel: string; count: number }[]
}

export type UseNewPatientsReturn = {
  filteredLocationRows: Jsonify<NewPatientsRow>[] | undefined
  tableColumns: ColumnDef<NewPatientsRow, any>[]
  stats: StatsCardType[]
  dateChangeHandler: (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => void
  setFilters: (filters: string) => void
  channel: string | undefined
  isSMC: boolean
  barsGroupedBy: `days` | `weeks` | `months`
  setBarsGroupedBy: (v: `days` | `weeks` | `months`) => void
  dayMetrics: NewPatientsMetrics[] | undefined
  newPatientsGrouped: GroupedData | undefined
  scheduledGrouped: ScheduledGroupedItem[] | undefined
  summaryMetrics: Partial<NewPatientsSummary> | undefined
  dontCompare: boolean
  isComparingToGoals: boolean
  budgetByMonth: Record<string, number>
  groupByChannelOrTreatment: `channel` | `treatment`
  graphContainerRef: RefObject<HTMLDivElement | null>
  isRequestInFlight: boolean
  fromDate: string | undefined
  toDate: string | undefined
  limitDateRangeToMonth: boolean
}

export function useNewPatients(): UseNewPatientsReturn {
  const user = useUser()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const locationIdsParam = searchParams.get(`locationIds`)
  const locationIds = useMemo(() => compact(locationIdsParam?.split(`,`) ?? []), [locationIdsParam])
  const integrationStatus = useMemo(() => searchParams.get(`integrationStatus`) ?? ``, [searchParams])
  const [fromDate, setFromDate] = useState<string>()
  const [toDate, setToDate] = useState<string>()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [groupByChannelOrTreatment, setGroupByChannelOrTreatment] = useState<`channel` | `treatment`>(`channel`)
  const [compareFromDate, setCompareFromDate] = useState<string>()
  const [compareToDate, setCompareToDate] = useState<string>()
  const [query, setQuery] = useState(searchParams.get(`query`))
  const [filters, setFilters] = useState<string>(searchParams.get(`dashboardFilters`) ?? ``)
  const [locationRows, setLocationRows] = useState<Jsonify<NewPatientsRow>[]>()
  const [filteredLocationRows, setFilteredLocationRows] = useState<Jsonify<NewPatientsRow>[]>()
  const [summaryMetrics, setSummaryMetrics] = useState<Partial<NewPatientsSummary>>()
  const [comparisonSummaryMetrics, setComparisonSummaryMetrics] = useState<Partial<NewPatientsSummary>>()
  const [stats, setStats] = useState<StatsCardType[]>([])
  const [filterContactsBy, setFilterContactsBy] = useState(searchParams.get(`details`))
  const [campaign, setCampaign] = useState<string[] | undefined>()
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const [dayMetrics, setDayMetrics] = useState<NewPatientsMetrics[]>()
  const [comparisonMetrics, setComparisonMetrics] = useState<NewPatientsMetrics[]>()
  const [scheduledDayMetrics, setScheduledDayMetrics] = useState<ScheduledMetric[]>()
  const [barsGroupedBy, setBarsGroupedBy] = useState<`days` | `weeks` | `months`>(`months`)
  const [isRequestInFlight, setIsRequestInFlight] = useState(false)
  const [channel, setChannel] = useState<string | undefined>(``)
  const platform: string | undefined = searchParams.get(`platform`) || ``
  const [dontCompare, setDontCompare] = useState<boolean>(
    searchParams.get(`compareSelectorPreset`) === `none` || !searchParams.get(`compareSelectorPreset`),
  )
  const [isComparingToGoals, setIsComparingToGoals] = useState(false)
  const [budgetByMonth, setBudgetByMonth] = useState<Record<string, number>>({})

  const newPatientsGrouped: GroupedData | undefined = useMemo(() => {
    if (typeof dayMetrics === `undefined` || typeof comparisonMetrics === `undefined`) {
      return { byChannel: [], byChannelYoY: [] }
    }
    const effectiveBarsGroupedBy =
      barsGroupedBy !== `days` && dayMetrics.length <= 7
        ? `days`
        : (barsGroupedBy === `days` || barsGroupedBy === `weeks`) && dayMetrics.length > 31
          ? `months`
          : barsGroupedBy
    if (effectiveBarsGroupedBy === `days`) {
      return {
        byChannel: dayMetrics?.map((day) => ({
          day: moment(day.bucket).format(`MMM D YYYY`),
          tooltipDisplay: moment(day.bucket).format(`MMM D, YYYY`),
          values: day.byChannel.reduce((acc, item) => ({ ...acc, [item.channel]: item.count }), {}),
        })),
        byChannelYoY: dayMetrics?.map((day) => {
          const comparisonDay = comparisonMetrics?.find((d) =>
            moment(d.bucket).isSame(moment(day.bucket).subtract(1, `year`), `day`),
          )
          return {
            day: moment(day.bucket).format(`MMM D YYYY`),
            tooltipDisplay: moment(day.bucket).subtract(1, `year`).format(`MMM D, YYYY`),
            values: comparisonDay?.byChannel.reduce((acc, item) => ({ ...acc, [item.channel]: item.count }), {}) ?? {},
          }
        }),
      }
    }

    const copyOfDayMetrics = cloneDeep(dayMetrics)
    const copyOfComparisonMetrics = cloneDeep(comparisonMetrics)

    if (effectiveBarsGroupedBy === `weeks` || effectiveBarsGroupedBy === `months`) {
      const groups = copyOfDayMetrics.reduce(
        (acc, day, index) => {
          const interval =
            effectiveBarsGroupedBy === `weeks`
              ? index === 0 ||
                (moment(copyOfDayMetrics.at(0)?.bucket, `YYYY-MM-DD`).isoWeek() ===
                  moment(day.bucket, `YYYY-MM-DD`).isoWeek() &&
                  moment(copyOfDayMetrics.at(0)?.bucket, `YYYY-MM-DD`).isoWeekYear() ===
                    moment(day.bucket, `YYYY-MM-DD`).isoWeekYear())
                ? moment(copyOfDayMetrics.at(0)?.bucket, `YYYY-MM-DD`).format(`MM-DD-YY`)
                : moment(day.bucket, `YYYY-MM-DD`).startOf(`week`).format(`MM-DD-YY`)
              : moment(day.bucket, `YYYY-MM-DD`).startOf(`month`).format(`MM-DD-YY`)

          if (acc[interval]) {
            acc[interval].byChannel = [...acc[interval].byChannel, ...day.byChannel].reduce(
              (acc, item) => {
                const existingItem = acc.find((a) => a.channel === item.channel)
                if (existingItem) {
                  existingItem.count += item.count
                } else {
                  acc.push(item)
                }
                return acc
              },
              [] as { channel: string; count: number }[],
            )
            const correspondingComparisonDay = copyOfComparisonMetrics.find((d) =>
              moment(d.bucket, `YYYY-MM-DD`).isSame(moment(day.bucket, `YYYY-MM-DD`).subtract(1, `year`)),
            ) ?? { byChannel: [] }
            acc[interval].byChannelYoY = [
              ...acc[interval].byChannelYoY,
              ...correspondingComparisonDay.byChannel,
            ].reduce(
              (acc, item) => {
                const existingItem = acc.find((a) => a.channel === item.channel)
                if (existingItem) {
                  existingItem.count += item.count
                } else {
                  acc.push(item)
                }
                return acc
              },
              [] as { channel: string; count: number }[],
            )
          } else {
            acc[interval] = {
              bucket: interval,
              byChannel: day.byChannel,
              byChannelYoY:
                copyOfComparisonMetrics.find((d) => moment(d.bucket).isSame(moment(day.bucket).subtract(1, `year`)))
                  ?.byChannel || [],
            }
          }
          return acc
        },
        {} as {
          [key: string]: {
            bucket: string
            byChannel: { channel: string; count: number }[]
            byChannelYoY: { channel: string; count: number }[]
          }
        },
      )

      const channelGroupedByInterval = Object.values(groups).map((group) => ({
        day:
          effectiveBarsGroupedBy === `weeks`
            ? moment(group.bucket, `MM-DD-YY`).format(`MMM D YYYY`)
            : moment(group.bucket, `MM-DD-YY`).format(`MMM YYYY`),
        tooltipDisplay:
          effectiveBarsGroupedBy === `weeks`
            ? `${moment(group.bucket, `MM-DD-YY`).format(`MMM D YYYY`)} - ${moment
                .min([moment(group.bucket, `MM-DD-YY`).endOf(`week`), moment(copyOfDayMetrics.at(-1)?.bucket)])
                .format(`MMM D`)} ${moment(group.bucket, `MM-DD-YY`).format(`YYYY`)}`
            : moment(group.bucket, `MM-DD-YY`).format(`MMMM YYYY`),
        values: group.byChannel.reduce((acc, item) => ({ ...acc, [item.channel]: item.count }), {}),
      }))
      const channelYoYGroupedByInterval = Object.values(groups).map((group) => ({
        day:
          effectiveBarsGroupedBy === `weeks`
            ? moment(group.bucket, `MM-DD-YY`).format(`MMM D YYYY`)
            : moment(group.bucket, `MM-DD-YY`).format(`MMM YYYY`),
        tooltipDisplay:
          effectiveBarsGroupedBy === `weeks`
            ? `${moment(group.bucket, `MM-DD-YY`).subtract(1, `year`).format(`MMM D YYYY`)} - ${moment
                .min([
                  moment(group.bucket, `MM-DD-YY`).endOf(`week`).subtract(1, `year`),
                  moment(copyOfDayMetrics.at(-1)?.bucket).subtract(1, `year`),
                ])
                .format(`MMM D`)} ${moment(group.bucket, `MM-DD-YY`).subtract(1, `year`).format(`YYYY`)}`
            : moment(group.bucket, `MM-DD-YY`).subtract(1, `year`).format(`MMMM YYYY`),
        values: group.byChannelYoY.reduce((acc, item) => ({ ...acc, [item.channel]: item.count }), {}),
      }))
      return { byChannel: channelGroupedByInterval, byChannelYoY: channelYoYGroupedByInterval }
    }
  }, [dayMetrics, comparisonMetrics, barsGroupedBy])

  const scheduledGrouped: ScheduledGroupedItem[] | undefined = useMemo(() => {
    if (!scheduledDayMetrics || !dayMetrics) return undefined
    if (barsGroupedBy === `days`) {
      return scheduledDayMetrics.map((d) => ({
        day: moment(d.bucket).format(`MMM D YYYY`),
        value: d.count,
        byChannel: d.byChannel,
      }))
    }
    const groups: {
      [key: string]: { value: number; minDate: string; maxDate: string; byChannel: { [channel: string]: number } }
    } = {}
    scheduledDayMetrics.forEach((d) => {
      const interval =
        barsGroupedBy === `weeks`
          ? moment(d.bucket, `YYYY-MM-DD`).startOf(`week`).format(`MM-DD-YY`)
          : moment(d.bucket, `YYYY-MM-DD`).startOf(`month`).format(`MM-DD-YY`)
      if (!groups[interval]) {
        groups[interval] = { value: 0, minDate: d.bucket, maxDate: d.bucket, byChannel: {} }
      }
      if (d.bucket < groups[interval].minDate) groups[interval].minDate = d.bucket
      if (d.bucket > groups[interval].maxDate) groups[interval].maxDate = d.bucket
      groups[interval].value += d.count
      d.byChannel.forEach((ch) => {
        groups[interval].byChannel[ch.channel] = (groups[interval].byChannel[ch.channel] || 0) + ch.count
      })
    })
    return Object.entries(groups).map(([key, group]) => {
      const minDisplay = moment(group.minDate).format(`MMM D`)
      const maxDisplay = moment(group.maxDate).format(`MMM D YYYY`)
      const tooltipDisplay = group.minDate === group.maxDate ? maxDisplay : `${minDisplay} - ${maxDisplay}`
      return {
        day:
          barsGroupedBy === `weeks`
            ? moment(key, `MM-DD-YY`).format(`MMM D YYYY`)
            : moment(key, `MM-DD-YY`).format(`MMM YYYY`),
        tooltipDisplay,
        value: group.value,
        byChannel: Object.entries(group.byChannel).map(([channel, count]) => ({ channel, count })),
      }
    })
  }, [scheduledDayMetrics, dayMetrics, barsGroupedBy])

  const handleCloseModal = () => setFilterContactsBy(null)
  const defaultFilters = {
    ...(searchParams.has(`platform`) && searchParams.get(`platform`) !== ``
      ? {
          [`platform`]: { operator: `exactly` as FilterOperator, value: searchParams.get(`platform`), readOnly: true },
        }
      : undefined),
    ...(searchParams.has(`dashboardFilters`) && searchParams.get(`dashboardFilters`) !== ``
      ? decodeFilters(searchParams.get(`dashboardFilters`)!).reduce(
          (acc, filter) => ({
            ...acc,
            [filter.attribute]: { operator: filter.operator as FilterOperator, value: filter.value, readOnly: true },
          }),
          {},
        )
      : undefined),
    ...(searchParams.has(`channel`)
      ? {
          [`channel.keyword`]: {
            operator: `in` as FilterOperator,
            value: [searchParams.get(`channel`)!],
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`locationIds`)
      ? {
          [`locationId`]: {
            operator: `in` as FilterOperator,
            value: compact(decodeURIComponent(searchParams.get(`locationIds`)!).split(`,`)),
            readOnly: true,
          },
        }
      : undefined),
    ...(searchParams.has(`fromDate`) && searchParams.has(`toDate`)
      ? {
          [`becamePatientDate`]: {
            operator: `between` as FilterOperator,
            value: [searchParams.get(`fromDate`)!, searchParams.get(`toDate`)!],
            readOnly: true,
          },
        }
      : defaultBecameLeadDateFilter),
  }

  useEffect(() => {
    if (!fromDate || !toDate) return
    debug(`pats`, `fetching`, {
      fromDate,
      toDate,
      compareFromDate,
      compareToDate,
      locationIds,
      channel,
      integrationStatus,
      platform,
      filters,
    })
    bg(async () => {
      setIsRequestInFlight(true)
      const { data, error } = await gqlQuery(NEW_PATIENTS_METRICS_QUERY, {
        dateRange: { fromDate, toDate },
        compareRange:
          compareFromDate && compareToDate ? { fromDate: compareFromDate, toDate: compareToDate } : undefined,
        channel: channel || undefined,
        locationIds: locationIds.length ? locationIds : undefined,
        integrationStatus: integrationStatus || undefined,
        filters: filters || undefined,
        platform: platform || undefined,
        hideMultiLocationPatients: searchParams.get(`hideMultiLocationPatients`) === `true`,
      })
      setIsRequestInFlight(false)
      if (error) {
        debug(`pats`, `error fetching new patients metrics`, error.message)
        return
      }
      if (data?.newPatientsMetrics) {
        const m = data.newPatientsMetrics
        setSummaryMetrics(m.summary)
        setComparisonSummaryMetrics(m.comparisonSummary ?? undefined)
        setDayMetrics(m.dailyMetrics)
        setComparisonMetrics(m.comparisonMetrics)
        setScheduledDayMetrics(m.dailyScheduledMetrics)
        // NewPatientsRow predates the typed document and declares fields this
        // selection never asked for; the table reads only the selected ones.
        setLocationRows(uniqBy(m.rows, (i) => i.location?.name) as Jsonify<NewPatientsRow>[])
      }
    })
    if (filters) {
      setCampaign(convertUrlFiltersToES(filters))
    } else setCampaign(undefined)
  }, [
    fromDate,
    toDate,
    compareFromDate,
    compareToDate,
    locationIds,
    channel,
    integrationStatus,
    platform,
    filters,
    searchParams.get(`hideMultiLocationPatients`),
  ])

  // Fetch budget goals per month when comparing to goals
  useEffect(() => {
    if (!isComparingToGoals || !fromDate || !toDate) {
      setBudgetByMonth({})
      return
    }
    const months: string[] = []
    let cursor = moment(fromDate).startOf(`month`)
    const end = moment(toDate).endOf(`month`)
    while (cursor.isSameOrBefore(end)) {
      months.push(cursor.format(`YYYY-MM`))
      cursor.add(1, `month`)
    }
    const locs = locationIds.length
      ? locationIds
      : ((locationRows ?? []).map((r) => r.bucket).filter(Boolean) as string[])
    if (!locs.length || !months.length) return

    const aliases = locs.flatMap((loc, li) => months.map((ym, mi) => ({ alias: `b_${li}_${mi}`, loc, ym })))
    const queryFields = aliases
      .map((a) => `${a.alias}: budgets(locationId: "${a.loc}", yearMonth: "${a.ym}") { patientGoal yearMonth }`)
      .join(`\n`)

    bg(async () => {
      const { data } = await gqlQuery<Record<string, { patientGoal?: number; yearMonth: string }[]>>(
        `{ ${queryFields} }`,
      )
      if (!data) return
      const monthMap: Record<string, number> = {}
      for (const results of Object.values(data)) {
        for (const b of results) {
          if (b.patientGoal && b.patientGoal > 0) {
            monthMap[b.yearMonth] = (monthMap[b.yearMonth] ?? 0) + b.patientGoal
          }
        }
      }
      setBudgetByMonth(monthMap)
    })
  }, [isComparingToGoals, fromDate, toDate, locationIds.join(`,`), locationRows])

  useEffect(() => {
    debug(`pats`, `searchParams changed`, searchParams)
    if (searchParams.has(`channel`)) {
      debug(`channel`, `setting channel from`, channel, `to`, searchParams.get(`channel`))
      setChannel(searchParams.get(`channel`) ?? ``)
    }
    if (searchParams.has(`query`)) {
      setQuery(searchParams.get(`query`) ?? ``)
    }
  }, [searchParams.get(`query`), searchParams.get(`channel`)])

  useEffect(() => {
    if (query && locationRows) {
      setFilteredLocationRows(
        clone(
          locationRows.filter((row) =>
            // nosemgrep
            query.split(` `).every((term) => row.location?.name?.match(new RegExp(term, `i`))),
          ),
        ),
      )
    } else {
      setFilteredLocationRows(clone(locationRows))
    }
  }, [query, locationRows])

  useEffect(() => {
    if (dontCompare) {
      debug(`pats`, `dont compare`)
      setCompareFromDate(undefined)
      setCompareToDate(undefined)
      setComparisonMetrics([])
    }
  }, [dontCompare])

  useEffect(() => {
    if (summaryMetrics) {
      setStats([
        {
          label: `New Patients`,
          value: `${summaryMetrics?.completed?.toLocaleString(`en-US`)}`,
          trend: comparisonSummaryMetrics?.completed
            ? { previous: comparisonSummaryMetrics.completed, current: summaryMetrics!.completed! }
            : undefined,
          // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
          onOpenClick: () => openModal(`New Patients`),
          infoTooltip: {
            title: `New Patients`,
            message: `All contacts who have completed a first appointment within the date range.`,
          },
        },
        {
          label: `Scheduled New Patients`,
          value: `${summaryMetrics.scheduled?.toLocaleString(`en-US`)}`,
          trend: comparisonSummaryMetrics?.scheduled
            ? { previous: comparisonSummaryMetrics.scheduled, current: summaryMetrics.scheduled! }
            : undefined,
          onOpenClick: () => openModal(`Scheduled New Patients`),
          infoTooltip: {
            title: `Scheduled New Patients`,
            message: `All contacts who have a first appointment scheduled in the future within the date range.`,
          },
        },
        {
          label: `Reappointment Rate`,
          value: `${formatPercent(summaryMetrics.reappointmentPercentage)}%`,
          trend: comparisonSummaryMetrics?.reappointmentPercentage
            ? {
                previous: comparisonSummaryMetrics.reappointmentPercentage,
                current: summaryMetrics.reappointmentPercentage!,
              }
            : undefined,
          onOpenClick: () => openModal(`Reappointment Rate`),
          infoTooltip: {
            title: `Reappointment Rate`,
            message: `Percentage of contacts that became a patient in the date range that left their appointment with a future appointment scheduled.`,
          },
        },
        {
          label: `Average Production per New Patient Visit`,
          value: `$${round(summaryMetrics?.averageProductionPerNewPatientVisit!)}`,
          trend: comparisonSummaryMetrics?.averageProductionPerNewPatientVisit
            ? {
                previous: comparisonSummaryMetrics.averageProductionPerNewPatientVisit,
                current: summaryMetrics.averageProductionPerNewPatientVisit!,
              }
            : undefined,
          infoTooltip: {
            title: `Average Production per New Patient Visit`,
            message: `The average gross production of the first visit for all contacts who have a became a patient date in the selected date range. The "first visit production" is calculated by adding up the procedure transaction values that are associated with the contacts first visit.`,
          },
        },
      ])
    }
  }, [summaryMetrics, comparisonMetrics])

  useEffect(() => {
    let currentParam = searchParams.get(`details`)
    if (filterContactsBy === currentParam) return
    handleSearchParams({
      details: filterContactsBy ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [filterContactsBy])

  useEffect(() => {
    if (
      filterContactsBy &&
      [`New Patients`, `Scheduled New Patients`, `Reappointment Rate`].includes(filterContactsBy) &&
      fromDate &&
      toDate
    ) {
      showModal(
        <MarketingContactTable
          title={filterContactsBy}
          channel={channel}
          filterContactsBy={filterContactsBy}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={locationIds}
          integrationStatus={integrationStatus}
          campaign={campaign}
          platform={platform}
          onClose={handleCloseModal}
          defaultFilters={defaultFilters}
          enableDeepLinking={false}
        />,
        { hideTitle: true, removePadding: true, isFullScreen: true, onClose: handleCloseModal, useRadix: true },
      )
    }
  }, [filterContactsBy, fromDate, toDate])

  const openModal = (type: ModalType) => {
    setFilterContactsBy(type)
  }

  const dateChangeHandler = (
    fromDate: string,
    toDate: string,
    compareFromDate: string,
    compareToDate: string,
    comparingGoals: boolean,
    dontCompare: boolean,
  ) => {
    debug(`pats`, `date change handler`, {
      dontCompare,
      comparingGoals,
      fromDate,
      toDate,
      compareFromDate,
      compareToDate,
    })
    setDontCompare(dontCompare)
    setIsComparingToGoals(comparingGoals)
    setFromDate(fromDate)
    setToDate(toDate)
    setCompareFromDate(compareFromDate)
    setCompareToDate(compareToDate)
  }

  const tableColumns = useMemo(() => defaultColumns(fromDate, toDate), [fromDate, toDate])

  return {
    filteredLocationRows,
    tableColumns,
    stats,
    dateChangeHandler,
    setFilters,
    channel,
    isSMC: isSMCOrg(user?.orgId),
    barsGroupedBy,
    setBarsGroupedBy,
    dayMetrics,
    newPatientsGrouped,
    scheduledGrouped,
    summaryMetrics,
    dontCompare,
    isComparingToGoals,
    budgetByMonth,
    groupByChannelOrTreatment,
    graphContainerRef,
    isRequestInFlight,
    fromDate,
    toDate,
    limitDateRangeToMonth: isSMCOrg(user?.orgId),
  }
}
